import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  ShieldExclamationIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

const confirmButtonStyles = {
  success:
    "bg-green-600 hover:bg-green-500 focus-visible:outline focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-green-600",
  error:
    "bg-red-600 hover:bg-red-500 focus-visible:outline focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-600",
  warning:
    "bg-orange-600 hover:bg-orange-500 focus-visible:outline focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-orange-600",
};

const iconCircleStyles = {
  success: "bg-green-100",
  error: "bg-red-50",
  warning: "bg-orange-100",
};

const ConfirmationModal = ({
  title,
  description,
  confirmText,
  cancelText,
  selectedId = null,
  openTrigger = false,
  closeTrigger = false,
  confirm = false,
  showCancelButton = true,
  showConfirmButton = true,
  disableConfirmButton = false,
  type = "error",
  width = "",
  height = "",
  content = "",
  loading = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(openTrigger);
  }, [openTrigger, closeTrigger]);

  const handleConfirm = () => {
    if (typeof confirm === "function") {
      confirm(selectedId);
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (typeof closeTrigger === "function") {
      closeTrigger();
    }
  };

  const resolvedType = confirmButtonStyles[type] ? type : "error";

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-100" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-gray-900/55 backdrop-blur-[1px] transition-opacity"
            aria-hidden="true"
          />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`relative w-full transform overflow-hidden rounded-xl bg-white text-left shadow-2xl ring-1 ring-gray-200/90 transition-all sm:my-8 sm:max-w-lg ${width} ${height}`}
              >
                <div className="px-4 pb-2 pt-6 sm:px-6 sm:pb-4 sm:pt-6">
                  <div className="sm:flex sm:items-start sm:gap-4">
                    <div
                      className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 ${iconCircleStyles[resolvedType]}`}
                    >
                      {resolvedType === "error" && (
                        <ExclamationTriangleIcon
                          className="h-6 w-6 text-red-600"
                          aria-hidden="true"
                        />
                      )}
                      {resolvedType === "success" && (
                        <CheckBadgeIcon
                          className="h-6 w-6 text-green-600"
                          aria-hidden="true"
                        />
                      )}
                      {resolvedType === "warning" && (
                        <ShieldExclamationIcon
                          className="h-6 w-6 text-orange-600"
                          aria-hidden="true"
                        />
                      )}
                    </div>

                    <div className="mt-4 text-center sm:mt-0 sm:flex-1 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold leading-6 text-gray-900"
                      >
                        {title}
                      </Dialog.Title>
                      <div className="mt-2 text-sm leading-relaxed text-gray-600">
                        {description ? <p>{description}</p> : null}
                        {content}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-100 bg-gray-50/95 px-4 py-4 sm:px-6">
                  <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    {showCancelButton && (
                      <button
                        type="button"
                        className="inline-flex w-full justify-center rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary-500 sm:w-auto"
                        onClick={handleClose}
                      >
                        {cancelText || t("common.cancel")}
                      </button>
                    )}
                    {showConfirmButton && (
                      <button
                        type="button"
                        className={`inline-flex w-full justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm sm:w-auto ${
                          disableConfirmButton || loading
                            ? "cursor-not-allowed bg-gray-400 hover:bg-gray-400 focus-visible:ring-0"
                            : confirmButtonStyles[resolvedType]
                        }`}
                        onClick={handleConfirm}
                        disabled={loading || disableConfirmButton}
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <svg
                              className="h-4 w-4 animate-spin text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            {t("common.processing")}…
                          </span>
                        ) : (
                          confirmText || t("common.ok")
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ConfirmationModal;
